import type { AssigneeSearchResult } from '@auth/entities/account';
import type {
  TaskPriority,
  TaskProfile,
  TaskTimerDirection,
  TaskTimerReference,
} from '@travel-planner/shared/schemas/tasks';
import type { ItinerarySearchResult } from '../api/use-itinerary-search';
import type { TaskFormValues } from '../model/schema';
import { useAssigneeSearch } from '@auth/entities/account';
import { useAuthData } from '@auth/features/auth';
import { UTCDate } from '@date-fns/utc';
import { coerceFlag, determineFlagForTask } from '@task-manager/entities/tasks';
import { TaskCollectionSelect } from '@task-manager/features/task-collection-select';
import { useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import {
  Button,
  DateTimePicker,
  Input,
  Select,
  ServerSearchSelect,
  Switch,
  Text,
  Textarea,
} from '@/shared/ui';
import { useItinerarySearch } from '../api/use-itinerary-search';
import {
  DURATION_HINT,
  defaultTaskFormValues,
  isValidDuration,
  taskFormSchema,
} from '../model/schema';
import css from './task-form.module.scss';

/**
 * Subset of `Task` (or its DB row equivalent) that the form needs to
 * pre-fill values. Using a structural type avoids coupling to the exact
 * server return type — `useTaskById` returns a Drizzle row whose `flag`
 * widens to `number | null`, while the shared schema's `Task.flag` is
 * `0 | 5 | 10 | null`.
 */
export type TaskFormInitial = {
  title: string;
  profile: TaskProfile;
  priority: TaskPriority;
  timerReference: TaskTimerReference;
  assignedAccountId: string | null;
  assignedAccountName?: string | null;
  duration: string | null;
  timerDirection: TaskTimerDirection | null;
  description: string | null;
  itineraryId: string | null;
  itineraryLabel?: string | null;
  collectionId: string | null;
  flag: number | null;
  expireOn: string | null;
};

const PROFILE_OPTIONS = [
  { value: 'consultant', label: 'Consultant' },
  { value: 'finance', label: 'Finance' },
  { value: 'operation', label: 'Operation' },
] as const;

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

const TIMER_DIRECTION_OPTIONS = [
  { value: 'before', label: 'before' },
  { value: 'after', label: 'after' },
] as const;

// Legacy excludes `custom_date` from the timer-reference dropdown — that
// branch is driven by the "Use custom date" toggle instead.
const TIMER_REFERENCE_OPTIONS = [
  { value: 'start_of_trip', label: 'start of trip' },
  { value: 'itinerary_creation', label: 'itinerary creation' },
  { value: 'last_change_date', label: 'last change date' },
] as const;

// Today's date as `yyyy-MM-dd` (UTC, the codebase wall-clock convention).
// Used as the date picker's `min` so past days can't be chosen. Schema
// validation is still the authoritative past-date gate.
const todayIso = (): string => {
  const d = new UTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
    d.getUTCDate()
  )}`;
};

type TaskFormProps = {
  initial?: TaskFormInitial;
  onSubmit: (values: TaskFormValues) => void;
  onCancel: () => void;
  submitLabel?: string;
  submitting?: boolean;
  /**
   * `'admin'` builds a **template** task (legacy admin TaskForm): shows a
   * Collection picker, hides Assignee + Itinerary, and submits with no
   * assignee so the task joins the collection's template library.
   */
  variant?: 'default' | 'admin';
};

const fromTask = (task: TaskFormInitial): TaskFormValues => ({
  title: task.title,
  profile: task.profile,
  priority: task.priority,
  timerReference: task.timerReference,
  assignedAccountId: task.assignedAccountId ?? undefined,
  duration: task.duration ?? undefined,
  timerDirection: task.timerDirection ?? undefined,
  description: task.description ?? undefined,
  itineraryId: task.itineraryId ?? undefined,
  collectionId: task.collectionId ?? undefined,
  flag: coerceFlag(task.flag),
  expireOn: task.expireOn ?? undefined,
});

export const TaskForm = ({
  initial,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  submitting = false,
  variant = 'default',
}: TaskFormProps) => {
  const isAdmin = variant === 'admin';
  const { data: authData } = useAuthData();
  const currentUserId = authData?.user.id;
  const currentUserName = authData?.user
    ? `${authData.user.firstName ?? ''} ${authData.user.lastName ?? ''}`.trim()
    : '';

  const [values, setValues] = useState<TaskFormValues>(() => {
    if (initial) return fromTask(initial);
    // New task: default the assignee to the current user (legacy parity).
    // Admin templates have no assignee and use a relative timer (their
    // due date is resolved later when cloned onto an itinerary), so they
    // default to `start_of_trip` rather than the custom-date path.
    return {
      ...defaultTaskFormValues,
      assignedAccountId: isAdmin ? undefined : currentUserId,
      timerReference: isAdmin
        ? 'start_of_trip'
        : defaultTaskFormValues.timerReference,
    };
  });
  const [error, setError] = useState<string | null>(null);

  // UI-only toggle. On a new task the form opens on the custom-date path
  // (no itinerary); on edit it mirrors the persisted timer reference.
  // Admin templates always use the relative timer (resolved on clone).
  const [useCustomDate, setUseCustomDate] = useState<boolean>(
    isAdmin ? false : initial ? initial.timerReference === 'custom_date' : true
  );

  // Pre-resolved labels for the searchable selects (so the closed trigger
  // shows a name/title without firing the search). Itinerary keeps its
  // last-selected label locally; the assignee label is resolved from the
  // org roster below so it stays correct on edit and after selection.
  const [itineraryLabel, setItineraryLabel] = useState<string | null>(
    initial?.itineraryLabel ?? null
  );

  // Org roster (cached by TanStack Query — `ServerSearchSelect` reuses the
  // same query), used to resolve the selected assignee id back to a name.
  const { data: roster } = useAssigneeSearch('', true);
  const assigneeLabel =
    (values.assignedAccountId
      ? roster?.find((a) => a.id === values.assignedAccountId)?.fullName
      : null) ??
    initial?.assignedAccountName ??
    (values.assignedAccountId === currentUserId ? currentUserName : null);

  const set = <K extends keyof TaskFormValues>(
    key: K,
    value: TaskFormValues[K] | undefined
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  const hasItinerary = !!values.itineraryId;
  // Legacy rule: custom date+time picker when the user opted in via the
  // toggle, OR (non-admin) when there's no itinerary to anchor a relative
  // timer against. Admin templates default to the relative timer but can
  // toggle to a custom date.
  const showCustomDate = isAdmin
    ? useCustomDate
    : useCustomDate || !hasItinerary;

  // Inline Jira-style validation for the Duration field (only relevant on
  // the timer path). Invalid once the user has typed something that isn't
  // a valid duration; empty is "incomplete", not flagged red mid-typing.
  const durationValue = values.duration ?? '';
  const durationInvalid =
    !showCustomDate &&
    durationValue.trim().length > 0 &&
    !isValidDuration(durationValue);

  // Empty-query itinerary list (cached by TanStack Query) — used to
  // resolve a freshly selected itinerary id back to its label.
  const { data: itineraryList } = useItinerarySearch('', true);

  const handleItineraryChange = (id: string | null) => {
    setValues((prev) => ({
      ...prev,
      itineraryId: id ?? undefined,
      // Selecting an itinerary resets to the relative-timer path.
      timerReference: id ? 'start_of_trip' : 'custom_date',
    }));
    setItineraryLabel(
      id ? (itineraryList?.find((i) => i.id === id)?.label ?? null) : null
    );
    if (id) setUseCustomDate(false);
  };

  const handleUseCustomDate = (checked: boolean) => {
    setUseCustomDate(checked);
    set('timerReference', checked ? 'custom_date' : 'start_of_trip');
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setError(null);
    // Admin templates must belong to a collection (legacy parity) — that
    // linkage is the only way a template surfaces under a collection.
    if (isAdmin && !values.collectionId) {
      setError('Collection is required');
      return;
    }
    const result = taskFormSchema.safeParse(values);
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? 'Invalid input');
      return;
    }
    // Flag computation (legacy `determineFlagForTask`) — TCT03-001 / TCT03-002.
    const flag = determineFlagForTask(
      result.data.assignedAccountId,
      currentUserId
    );
    onSubmit({ ...result.data, flag });
  };

  return (
    <form className={css.root} onSubmit={handleSubmit}>
      <div className={css.field}>
        <Text as="span" size="sm" weight="medium">
          Title
        </Text>
        <Input
          autoFocus
          onChange={(e) => set('title', e.target.value)}
          placeholder="Enter task title"
          required
          value={values.title}
        />
      </div>

      {isAdmin ? null : (
        <div className={css.field}>
          <Text as="span" size="sm" weight="medium">
            Assigned to
          </Text>
          <ServerSearchSelect<AssigneeSearchResult>
            getOptionLabel={(item) => item.fullName}
            getOptionValue={(item) => item.id}
            onChange={(id) => set('assignedAccountId', id ?? undefined)}
            placeholder="Select assignee"
            searchPlaceholder="Search by name…"
            selectedLabel={assigneeLabel}
            useSearch={useAssigneeSearch}
            usePortal
            value={values.assignedAccountId ?? null}
          />
        </div>
      )}

      <div className={css.field}>
        <Text as="span" size="sm" weight="medium">
          Profile
        </Text>
        <Select
          onChange={(e) =>
            set('profile', e.target.value as TaskFormValues['profile'])
          }
          options={
            PROFILE_OPTIONS as unknown as { value: string; label: string }[]
          }
          usePortal
          value={values.profile}
        />
      </div>

      <div className={css.field}>
        <Text as="span" size="sm" weight="medium">
          Priority
        </Text>
        <Select
          onChange={(e) =>
            set('priority', e.target.value as TaskFormValues['priority'])
          }
          options={
            PRIORITY_OPTIONS as unknown as { value: string; label: string }[]
          }
          usePortal
          value={values.priority}
        />
      </div>

      {isAdmin ? null : (
        <div className={css.field}>
          <Text as="span" size="sm" weight="medium">
            Itinerary
          </Text>
          <ServerSearchSelect<ItinerarySearchResult>
            getOptionLabel={(item) => item.label}
            getOptionValue={(item) => item.id}
            onChange={handleItineraryChange}
            placeholder="Select itinerary"
            searchPlaceholder="Search itinerary by title, ref…"
            selectedLabel={itineraryLabel}
            useSearch={useItinerarySearch}
            usePortal
            value={values.itineraryId ?? null}
          />
        </div>
      )}

      {isAdmin || hasItinerary ? (
        <div className={css.field}>
          <div className={css.toggleRow}>
            <Text as="span" size="sm" weight="medium">
              Use custom date
            </Text>
            <Switch
              aria-label="Use custom date"
              checked={useCustomDate}
              onCheckedChange={handleUseCustomDate}
            />
          </div>
        </div>
      ) : null}

      {showCustomDate ? (
        <div className={css.field}>
          <Text as="span" size="sm" weight="medium">
            Expire On
          </Text>
          <DateTimePicker
            minDate={todayIso()}
            onChange={(iso) => set('expireOn', iso)}
            value={values.expireOn}
          />
        </div>
      ) : (
        <div className={css.field}>
          <Text as="span" size="sm" weight="medium">
            Due date
          </Text>
          <div className={css.timerRow}>
            <Text as="span" size="sm" variant="muted">
              Due date is
            </Text>
            <Input
              aria-invalid={durationInvalid}
              className={joinCss(
                css.timerDuration,
                durationInvalid && css.timerDurationInvalid
              )}
              onChange={(e) => set('duration', e.target.value)}
              placeholder="1d"
              value={values.duration ?? ''}
            />
            <Select
              className={css.timerDirection}
              onChange={(e) =>
                set(
                  'timerDirection',
                  e.target.value as TaskFormValues['timerDirection']
                )
              }
              options={
                TIMER_DIRECTION_OPTIONS as unknown as {
                  value: string;
                  label: string;
                }[]
              }
              usePortal
              value={values.timerDirection ?? 'before'}
            />
            <Text as="span" size="sm" variant="muted">
              the
            </Text>
            <Select
              className={css.timerReference}
              onChange={(e) =>
                set(
                  'timerReference',
                  e.target.value as TaskFormValues['timerReference']
                )
              }
              options={
                TIMER_REFERENCE_OPTIONS as unknown as {
                  value: string;
                  label: string;
                }[]
              }
              usePortal
              value={
                values.timerReference === 'custom_date'
                  ? 'start_of_trip'
                  : values.timerReference
              }
            />
          </div>
          <Text
            as="p"
            className={durationInvalid ? css.error : undefined}
            size="sm"
            variant={durationInvalid ? undefined : 'muted'}
          >
            {durationInvalid
              ? 'Invalid duration format. ' + DURATION_HINT
              : DURATION_HINT}
          </Text>
        </div>
      )}

      <div className={css.field}>
        <Text as="span" size="sm" weight="medium">
          Description
        </Text>
        <Textarea
          onChange={(e) => set('description', e.target.value)}
          placeholder="Enter description"
          rows={3}
          value={values.description ?? ''}
        />
      </div>

      {isAdmin ? (
        <div className={css.field}>
          <Text as="span" size="sm" weight="medium">
            Collection
          </Text>
          <TaskCollectionSelect
            onChange={(id) => set('collectionId', id || undefined)}
            placeholder="Select collection"
            usePortal
            value={values.collectionId}
          />
        </div>
      ) : null}

      {error ? (
        <Text as="p" className={css.error} size="sm">
          {error}
        </Text>
      ) : null}

      <div className={css.footer}>
        <Button
          disabled={submitting}
          onClick={onCancel}
          size="sm"
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button disabled={submitting} size="sm" type="submit" variant="primary">
          {submitting ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
};
